import { datadogRum } from "@datadog/browser-rum";
import { unwrapConnection } from "@utils/utils";
import { useMemo, useState } from "react";

import {
  ApiReportReportStatusChoices,
  ApiReportTemplateExportFrameworkChoices,
  ReportStatusEnums,
  ReportStatusQuery,
  ReportTypeEnums,
  useCreateExportReportGenerationMutation,
  useCreateReportFromTemplateMutation,
  useReportStatusQuery,
  useSignReportMutation,
  useUpdateReportMutation,
  useUpdateReportStatusAndUploadReportFileMutation,
} from "@api/graphql/types-and-hooks";
import { SIGNATURE_SECTION_IDENTIFIER } from "@components/ReportField/consts";
import { notifyError } from "@components/notifications/notifications";
import { useInFlightOnce } from "@hooks/useInFlightOnce";
import { ReportNode } from "@providers/ReportProvider";

interface UseSignatureHandlerProps {
  report: ReportNode;
  isCompleted: boolean;
  refetchData: () => Promise<void> | unknown;
  // Fires after the polling loop exits regardless of final status
  // (Completed, Failure, or timeout). Used by consumers that need to clear UI
  // state like "isGenerating" no matter how the sign attempt ended.
  onSignSuccess?: () => void;
  // Fires only when the report transitions to Completed status. Used by
  // analytics that should count only true successes (e.g. report_completed event).
  onReportCompleted?: () => void;
  onSignError?: () => void;
}

export const useSignatureHandler = ({
  report,
  refetchData,
  onSignSuccess,
  onReportCompleted,
  onSignError,
}: UseSignatureHandlerProps) => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] = useState<boolean>(false);

  const signReportMutation = useSignReportMutation({});
  const updateStatusAndUploadMutation = useUpdateReportStatusAndUploadReportFileMutation({});
  const createFromTemplateMutation = useCreateReportFromTemplateMutation({});
  const generateTypescriptExportReport = useCreateExportReportGenerationMutation({});
  const updateReportStatusMutatoin = useUpdateReportMutation({});
  const { refetch: refetchReportStatus } = useReportStatusQuery({ reportId: report.id });

  const getLatestStatus = (data?: ReportStatusQuery) => data?.getReport?.edges?.[0]?.node?.reportStatus ?? "";

  const onSuccess = useMemo(() => {
    return async () => {
      let attempts = 0;
      const maxAttempts = 60;
      const pollingInterval = 3000;
      let reportStatus = "";
      while (attempts < maxAttempts) {
        const { data: reportStatusQuery } = await refetchReportStatus();
        reportStatus = getLatestStatus(reportStatusQuery);
        if (reportStatus !== ApiReportReportStatusChoices.Generating) {
          if (reportStatus === ApiReportReportStatusChoices.Failure) {
            notifyError("Report generation failed.");
          }
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, pollingInterval));
        attempts++;
      }
      if (attempts === maxAttempts && reportStatus === ApiReportReportStatusChoices.Generating) {
        setIsCompletionDialogOpen(false);
        notifyError("Report generation timed out.");
      }
      await refetchData();

      setIsGenerating(false);
      if (onSignSuccess) {
        onSignSuccess();
      }
      if (onReportCompleted && reportStatus === ApiReportReportStatusChoices.Completed) {
        onReportCompleted();
      }
    };
  }, [refetchData, refetchReportStatus, onSignSuccess, onReportCompleted]);

  const onError = useMemo(() => {
    return async (error: unknown) => {
      setIsGenerating(false);
      console.error("Error Saving Report", error);
      notifyError("Error Saving Report");
      if (onSignError) {
        onSignError();
      }
    };
  }, [onSignError]);

  const runSignReport = async (file?: File | null) => {
    // Signature-section fields (typed/uploaded/drawn) are ordinary report
    // fields, edited in place wherever the report renders its sections
    // (registry.tsx) — signReport only finalizes: it validates the
    // section's required fields (uniformly, via `is_required`, same as any
    // other field), stamps the signing date, and freezes the audit
    // snapshot. A report whose template has no signature section at all
    // has nothing to finalize here — signReport would just reject with
    // "no signature section to sign", so skip calling it rather than
    // blocking report completion over a template-configuration gap this
    // change doesn't otherwise fix.
    const hasSignatureSection = (unwrapConnection(report?.reportSections) ?? []).some(
      (section) => section?.sectionIdentifier === SIGNATURE_SECTION_IDENTIFIER,
    );

    if (hasSignatureSection) {
      try {
        await signReportMutation.mutateAsync({
          input: {
            reportId: report?.id ?? "<missing-id>",
            // PROA-2891: the signer's zone, a display label for the date box
            // and export attestation. The server stamps the signing time.
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
          },
        });
      } catch (error) {
        // Unlike the legacy mutation this replaces, `signReport` actually
        // validates (required fields) — a rejection here means the report
        // was NOT signed, so don't fall through to marking it
        // complete/exported below.
        console.error("Error signing report: ", error);
        notifyError("Error signing report");
        setIsGenerating(false);
        // PROA-2622: this is the most common way signing fails (a required
        // field), and it was the one error path that never reached
        // `onSignError` -- it resolves silently, so consumers could not tell it
        // from a success. ReportPage uses the callback to close its completion
        // dialog, which until now stayed open over an unsigned report.
        if (onSignError) {
          onSignError();
        }
        return;
      }
    }

    // log completion to datadog
    const base64DecodedId = atob(report.id);
    const databaseId = base64DecodedId.split("ReportType:")?.pop();
    datadogRum.addAction("report-completion", {
      reportId: databaseId,
      reportType: report.reportType,
    });

    const exportFramework = report.reportTemplate?.exportFramework;
    const isUnifiedExport = report.reportTemplate?.unifiedExport ?? false;
    // The template's `unifiedExport` field forces docxtemplater orgs through the
    // handlebars server-side generation pipeline at sign time, so the .docx/.pdf
    // is available in S3 when the user later clicks Export.
    const useHandlebarsGeneration =
      exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars ||
      (exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater && isUnifiedExport);

    // PROA-2622: every branch below awaits `onSuccess` rather than handing it to
    // `mutateAsync` as a per-call option. TanStack dispatches those through
    // MutationObserver without awaiting them, so as a mutate option the polling
    // loop runs detached: the promise settles on the mutation round-trip alone,
    // releasing the in-flight guard while generation still has minutes to go,
    // and anything thrown inside surfaces as an unhandled rejection.
    //
    // Doing this on only the handlebars branch would make the guard's coverage
    // depend on the template's export framework, which is not a distinction any
    // caller makes.
    if (useHandlebarsGeneration) {
      const result = await generateTypescriptExportReport.mutateAsync(
        {
          input: {
            reportId: report?.id,
            learnerId: report?.learner?.id || "",
            reportType: report.reportType as unknown as ReportTypeEnums,
            docxExport: true,
          },
        },
        {
          onError: (error) => {
            console.error("Error generating typescript export:", error);
            notifyError("Error generating typescript export");
            if (onSignError) {
              onSignError();
            }
          },
        },
      );
      await onSuccess();
      return result;
    }

    if (exportFramework === ApiReportTemplateExportFrameworkChoices.GoogleDocs) {
      const result = await createFromTemplateMutation.mutateAsync(
        { reportId: report?.id || "" },
        {
          onError: (error) => {
            console.error("Error generating Google Docs export:", error);
            notifyError("Error generating export");
            if (onSignError) {
              onSignError();
            }
          },
        },
      );
      await onSuccess();
      return result;
    }

    if (exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater && !file) {
      // Docxtemplater generates DOCX just-in-time on the frontend;
      // no file upload needed, just mark the report as completed.
      const result = await updateReportStatusMutatoin.mutateAsync(
        {
          reportId: report?.id || "",
          status: ReportStatusEnums.Completed,
        },
        { onError },
      );
      await onSuccess();
      return result;
    }

    // Old method of uploading the file
    const result = await updateStatusAndUploadMutation.mutateAsync(
      {
        reportId: report?.id || "",
        reportDocxFile: file,
      },
      { onError },
    );
    await onSuccess();
    return result;
  };

  // PROA-2622: a second click must not start a second export workflow. No UI
  // state gates it -- `isGenerating` only swaps the button label, and one call
  // site opens its loading dialog 2s late. Because every branch above awaits
  // its polling loop, this covers the whole run, not just the mutation
  // round-trip.
  const handleSignReport = useInFlightOnce(runSignReport);

  return {
    isGenerating,
    setIsGenerating,
    handleSignReport,
    isCompletionDialogOpen,
    setIsCompletionDialogOpen,
  };
};
